import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { Listing } from 'src/WebApiController';

import { ListingPricingInput } from './ListingPriceForm.types';

/**
 * Shared hook for determining if a field is currently being submitted
 */
export function useFieldIsSubmitting() {
  const { watch } = useFormContext<ListingPricingInput>();

  const isSubmittingPricingSettings = watch('isSubmittingPricingSettings');
  const isSubmittingPrice = watch('isSubmitting');
  const isSubmittingRowIndex = watch('isSubmittingRowIndex');
  const rowIndex = watch('rowIndex');

  return useMemo(
    () =>
      (isSubmittingPricingSettings || isSubmittingPrice) &&
      (isSubmittingRowIndex === undefined || isSubmittingRowIndex === rowIndex),
    [
      isSubmittingPricingSettings,
      isSubmittingPrice,
      isSubmittingRowIndex,
      rowIndex,
    ]
  );
}

/**
 * Shared hook for determining if a cell is disabled
 */
export function useCellDisabled(
  listing: Listing,
  canAutoPrice: boolean,
  editable: boolean | undefined,
  eventVirtId: string,
  additionalDisabledConditions?: boolean
) {
  const { getSelection } = useMultiSelectionContext();
  const fieldIsSubmitting = useFieldIsSubmitting();

  const listingSelection = getSelection(eventVirtId);

  return useMemo(
    () =>
      listing.isDeleted ||
      !canAutoPrice ||
      listingSelection.isSingleGroupMultiSelect ||
      fieldIsSubmitting ||
      !editable ||
      (additionalDisabledConditions ?? false),
    [
      listing.isDeleted,
      canAutoPrice,
      listingSelection.isSingleGroupMultiSelect,
      fieldIsSubmitting,
      editable,
      additionalDisabledConditions,
    ]
  );
}

/**
 * Shared hook for determining if a specific field is being submitted
 */
export function useIsSubmittingField(fieldName: string) {
  const { watch } = useFormContext<ListingPricingInput>();
  const fieldIsSubmitting = useFieldIsSubmitting();
  const submittingField = watch('submittingField');

  return useMemo(
    () => fieldIsSubmitting && submittingField === fieldName,
    [fieldIsSubmitting, submittingField, fieldName]
  );
}
